import { sanitize } from "dompurify";
import { Formik, FormikActions } from "formik";
import * as React from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { GetFormattedDateStr, isNullOrWhiteSpace } from "../../../common/helper/helper";
import { getRcTokenAsync, tokenFailable } from "../../GoogleReCaptcha/Recaptcha";
import { IBusyActions } from "../../hooks/use-busy-state";
import { BPointTokenRequestModel } from "../../ml_modules/websiteApi/BPointTokenRequestModel";
import { PaymentProvider } from "../../ml_modules/websiteApi/PaymentProvider";
import { WaMembershipModel } from "../helper";
import "../Style/WaForm.scss";
import "../Style/WaMembershipForm.scss";
import {
	IFormSelectOption,
	IMemberShipFormProps,
	IWaMemberShipFormModel,
	MembershipFormType,
	WaFormFieldConstants,
	WaMembershipInitialValuesFactory,
} from "./Constants";
import { FirstLetterUpperCase, WaMembershipValiationSchemaFactory } from "./FunctionLib";
import { InnerForm } from "./InnerForm";
import {
	useWaMembershipFormContext,
	WaMembershipFormContextProvider,
} from "./WaMembershipFormContext";

export const message = {
	tokenError: "Unable to process payment. Please contact the website administrator.",
};

export interface MonthlyPaymentOption {
	paymentOptionDescription: string;
	paymentOptionAmount: number;
}

export interface IWaMembershipFormProps extends IMemberShipFormProps {
	formType: MembershipFormType;
	formSubmissionRedirectUrl?: string;
	localBranchList: IFormSelectOption[];
	directBranchList: IFormSelectOption[];
	unionList: IFormSelectOption[];
	monthlyPaymentOptions: MonthlyPaymentOption[];
	yearlyPaymentAmount: number;
	concessionCardDiscount: number;
	unionDiscount: number;
	paypalClientId: string;
}

export interface ICampaign {
	website_name: string;
	code: string;
	account: string;
}

const MembershipForm = (
	props: IWaMembershipFormProps
): React.FunctionComponentElement<IWaMembershipFormProps> => {
	const { executeRecaptcha } = useGoogleReCaptcha();
	const formContext = useWaMembershipFormContext();

	const submitFunction = async (
		values: any,
		actions: FormikActions<IWaMemberShipFormModel>,
		formType: MembershipFormType
	) => {
		const rcToken = getRcTokenAsync(`WA${formType}Form`, executeRecaptcha);
		actions.setSubmitting(true);
		let fixedValues: Partial<WaMembershipModel> = {};
		fixedValues.PhoneHome = values[WaFormFieldConstants.phone]?.replace(/\D/g, "");
		fixedValues.DateOfBirth = !isNullOrWhiteSpace(values[WaFormFieldConstants.dateOfBirth])
			? GetFormattedDateStr(values[WaFormFieldConstants.dateOfBirth], "DD/MM/YYYY")
			: "";
		if (formType != MembershipFormType.Update) {
			fixedValues.Signature = values[WaFormFieldConstants.signature];
			fixedValues.YearlyPayment = formContext.totalDue;
			fixedValues.Amount = formContext.totalDue;
			fixedValues.Concession = values[WaFormFieldConstants.concession];
			fixedValues.Application = formType == MembershipFormType.Join;
			fixedValues.Declaration = values[WaFormFieldConstants.declaration];
			fixedValues.PaymentProviderName = values[WaFormFieldConstants.paymentProvider];
			fixedValues.AdminComRef =
				values[WaFormFieldConstants.adminComRef1] === true ||
				values[WaFormFieldConstants.adminComRef2] === true ||
				values[WaFormFieldConstants.adminComRef3] === true ||
				values[WaFormFieldConstants.adminComRef4] === true ||
				values[WaFormFieldConstants.adminComRef5] === true ||
				values[WaFormFieldConstants.adminComRef6] === true;
			fixedValues.ExpiryDate = formContext.ccData.expiry;
			if (values[WaFormFieldConstants.firstTimeMember] === "yes") {
				fixedValues.FirstTimeMember = true;
			} else if (values[WaFormFieldConstants.firstTimeMember] !== "") {
				fixedValues.FirstTimeMember = false;
			} else {
				fixedValues.FirstTimeMember = undefined;
			}
			if (values[WaFormFieldConstants.electoralRoll] === "yes") {
				fixedValues.ElectoralRoll = true;
			} else if (values[WaFormFieldConstants.electoralRoll] !== "") {
				fixedValues.ElectoralRoll = false;
			} else {
				fixedValues.ElectoralRoll = undefined;
			}
			if (values[WaFormFieldConstants.aboriginalDescent] === "yes") {
				fixedValues.AboriginalDescent = true;
			} else if (values[WaFormFieldConstants.aboriginalDescent] !== "") {
				fixedValues.AboriginalDescent = false;
			} else {
				fixedValues.AboriginalDescent = undefined;
			}
			fixedValues.MonthlyPayment = values[WaFormFieldConstants.annualIncome];
			fixedValues.AnnualIncome = (
				props.monthlyPaymentOptions
					?.sort((a, b) => {
						return a.paymentOptionAmount - b.paymentOptionAmount;
					})
					?.findIndex(
						(option) =>
							option.paymentOptionAmount ===
							Number(values[WaFormFieldConstants.annualIncome])
					) + 1
			)?.toString();
		}
		Object.entries(values).forEach(([key, value]) => {
			if (
				key !== WaFormFieldConstants.electoralRoll &&
				key !== WaFormFieldConstants.sameAsResidential &&
				key !== WaFormFieldConstants.concession &&
				key !== WaFormFieldConstants.application &&
				key !== WaFormFieldConstants.firstTimeMember &&
				key !== WaFormFieldConstants.declaration &&
				key !== WaFormFieldConstants.adminComRef &&
				key !== WaFormFieldConstants.adminComRef1 &&
				key !== WaFormFieldConstants.adminComRef2 &&
				key !== WaFormFieldConstants.adminComRef3 &&
				key !== WaFormFieldConstants.adminComRef4 &&
				key !== WaFormFieldConstants.adminComRef5 &&
				key !== WaFormFieldConstants.adminComRef6 &&
				key !== WaFormFieldConstants.yearlyPayment &&
				key !== WaFormFieldConstants.signature &&
				key !== WaFormFieldConstants.monthlyPayment &&
				key !== WaFormFieldConstants.aboriginalDescent &&
				key !== WaFormFieldConstants.dateOfBirth &&
				key != WaFormFieldConstants.annualIncome &&
				key != WaFormFieldConstants.paymentProvider
			) {
				fixedValues[FirstLetterUpperCase(key)] = sanitize(values[key].trim(), {
					ALLOWED_TAGS: [],
				});
			}
		});
		const ContentId = parseInt(props.contentId, 10);
		formContext.busyStateActions.onStart();
		if (formType != MembershipFormType.Update) {
			const TokenRequest: BPointTokenRequestModel = {
				ContentId: ContentId.toString(),
				CardNum: formContext.ccData.number.toString(),
				ExpiryMonth: formContext.ccData.expiry.toString().substr(0, 2),
				ExpiryYear: formContext.ccData.expiry.toString().substr(-2),
				Errors: null,
				PaymentProvider: PaymentProvider.BPoint,
				CardName: values["FirstName"] + " " + values["LastName"],
			};
			const resp = await fetch("/Umbraco/Api/BPoint/CreateBPointToken", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(TokenRequest),
			});
			const token = await resp.json();
			const bPointToken = token.Token;
			const maskedCardNumber = token.MaskedCardNumber;
			if (
				bPointToken === "" ||
				maskedCardNumber === "" ||
				bPointToken === undefined ||
				maskedCardNumber === undefined
			) {
				actions.setSubmitting(false);
				formContext.busyStateActions.onError(message.tokenError);
			} else {
				const body = {
					...fixedValues,
					ContentId: ContentId,
					SecureToken: bPointToken,
					MaskedCardNumber: maskedCardNumber,
					RcToken: await tokenFailable(rcToken),
				};
				handleSubmitNResponse(actions, formContext.busyStateActions, body);
			}
		} else {
			const body = {
				...fixedValues,
				ContentId: ContentId,
				FormType: formType,
				RcToken: await tokenFailable(rcToken),
			};
			handleSubmitNResponse(actions, formContext.busyStateActions, body);
		}
	};

	const handleSubmitNResponse = async (
		actions: FormikActions<IWaMemberShipFormModel>,
		busyActions: IBusyActions,
		body: Object
	) => {
		const response = await fetch("/Umbraco/Api/WaMembership/Submit", {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		});
		if (props.formType == MembershipFormType.Update) {
			const result = await response;
			if (!result.ok) {
				busyActions.onError(result.statusText);
				actions.setSubmitting(false);
			} else {
				actions.resetForm();
				busyActions.onError(
					"Thanks for submitting your membership update request. We will be in touch if there are any issues."
				);
				formContext.setStepNum(formContext.stepNum + 1);
			}
		} else {
			const result = await response.json();
			if (result?.Success !== true) {
				busyActions.onError(result.Message);
				actions.setSubmitting(false);
			} else {
				actions.resetForm();
				busyActions.onError(result.Message);
				if (!isNullOrWhiteSpace(formContext.formSubmissionRedirectUrl)) {
					window.location.href = formContext.formSubmissionRedirectUrl;
				} else {
					formContext.setStepNum(formContext.stepNum + 1);
				}
			}
		}
	};

	return (
		<Formik<IWaMemberShipFormModel>
			initialValues={WaMembershipInitialValuesFactory(props.formType)}
			validationSchema={WaMembershipValiationSchemaFactory(props.formType)}
			onSubmit={(
				values: IWaMemberShipFormModel,
				formikActions: FormikActions<IWaMemberShipFormModel>
			) => submitFunction(values, formikActions, props.formType)}
			children={(renderProps) => {
				return <InnerForm
					{...renderProps}
					{...props}
					touched={{ ...renderProps.touched, [WaFormFieldConstants.adminComRefDetails]: true }}
				/>
			}}
		/>
	);
};

export const WaMemberShipForm = (
	props: IWaMembershipFormProps
): React.FunctionComponentElement<IWaMembershipFormProps> => {
	return (
		<WaMembershipFormContextProvider {...props}>
			<MembershipForm {...props} />
		</WaMembershipFormContextProvider>
	);
};
